import React, { useRef } from 'react';
import { DetailSections } from '../DetailSections/DetailSections';
import { RelatedProductCollectionsSection } from '../RelatedProductCollectionsSection';
import { ExtraArgs, ExtraArgsByType, PDPSection, isDetailsSectionType } from './PdpSections.utils';
import { StyledRoot, styles } from './PdpSections.styles';
import { PDPSectionType } from '@graphql/generated';
import { CatalogProductItemDetails } from '../../../../Catalog.types';

const SECTION_TYPE_MAPPER: Record<PDPSectionType, ((props: { section: PDPSection; isFirstSection: boolean; extraArgs: ExtraArgs }) => React.ReactElement | null) | null> = {
  [PDPSectionType.details]: null,
  [PDPSectionType.gallery]: null,
  [PDPSectionType.productAction]: null,
  [PDPSectionType.productFamilyCollections]: null,
  [PDPSectionType.relatedProductCollections]: RelatedProductCollectionsSection,
  [PDPSectionType.highlight]: null,
};

export const PdpSections = React.memo(
  ({
    sections,
    getSectionExtraArgsByType,
  }: {
    sections: NonNullable<CatalogProductItemDetails['pdpSections']>;
    getSectionExtraArgsByType: (sectionType: PDPSectionType) => ExtraArgs;
  }) => {
    const firstSectionTrackedRef = useRef(false);
    if (!sections?.length) return null;

    const detailsSections = sections.filter(isDetailsSectionType);
    let detailsSectionsWasRendered = false;

    const sectionComponents = sections
      .map((section, idx) => {
        const Section = SECTION_TYPE_MAPPER[section.sectionType];

        const isFirstSection = !firstSectionTrackedRef.current;
        firstSectionTrackedRef.current = true;
        const extraArgs = getSectionExtraArgsByType(section.sectionType);

        if (isDetailsSectionType(section) && !detailsSectionsWasRendered) {
          detailsSectionsWasRendered = true;
          return (
            <DetailSections
              key={`${section.sectionType}-${idx}`}
              sections={detailsSections}
              openFirstSection={isFirstSection}
              extraArgs={extraArgs as ExtraArgsByType<typeof section.sectionType>}
            />
          );
        }

        if (!Section) {
          return null;
        }

        return (
          <Section
            key={`${section.sectionType}-${idx}`}
            section={section}
            isFirstSection={isFirstSection}
            extraArgs={extraArgs as ExtraArgsByType<typeof section.sectionType>}
          />
        );
      })
      .filter(x => x);

    if (!sectionComponents.length) return null;

    return <StyledRoot __css={styles.root}>{sectionComponents}</StyledRoot>;
  },
);
